import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { Location } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import type { ApiError } from "../api/client";

type LocationState = { from?: Location };

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | undefined)?.from?.pathname ?? "/dashboard";

  // Set by AuthContext when a dead session forced the redirect here — read
  // once so the notice survives exactly one render of the login page.
  const [sessionExpired] = useState(() => {
    try {
      const flag = sessionStorage.getItem("bankom.sessionExpired") === "1";
      sessionStorage.removeItem("bankom.sessionExpired");
      return flag;
    } catch {
      return false;
    }
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaPending, setMfaPending] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password, rememberMe, mfaPending ? totpCode : undefined);
      if (result.mfaRequired) {
        setMfaPending(true);
        setTotpCode("");
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(translateError(apiErr));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell" style={shellStyle}>
      <form onSubmit={handleSubmit} style={cardStyle} noValidate>
        <div style={logoPlateStyle}>
          <img src="/assets/bankom-logo.svg" alt="Bankom Group" style={logoStyle} />
        </div>
        <h1 style={titleStyle}>Sign in</h1>
        <p style={subtitleStyle}>Bankom Partner Portal</p>

        {sessionExpired && !error && (
          <div style={{ ...errorStyle, background: "var(--brand-primary-50)", borderColor: "var(--info)", color: "var(--info)" }}>
            {t("login.sessionExpired", "Your session has expired. Please sign in again.")}
          </div>
        )}
        {error && <div style={errorStyle}>{error}</div>}

        <label style={labelStyle}>
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            style={inputStyle}
          />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          Remember me on this device
        </label>

        {mfaPending && (
          <label style={labelStyle}>
            <span>Authenticator code</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              autoComplete="one-time-code"
              required
              style={inputStyle}
            />
          </label>
        )}

        <button type="submit" disabled={submitting || (mfaPending && totpCode.length !== 6)} style={primaryButton}>
          {submitting ? "Signing in…" : mfaPending ? "Verify" : "Sign in"}
        </button>

        <Link to="/forgot-password" style={linkStyle}>
          Forgot your password?
        </Link>
      </form>
    </div>
  );
}

function translateError(err: ApiError): string {
  switch (err.code) {
    case "INVALID_CREDENTIALS":
      return "Invalid email or password.";
    case "ACCOUNT_LOCKED":
      return "Your account is temporarily locked. Try again later or reset your password.";
    case "ACCOUNT_DISABLED":
      return "This account is disabled. Please contact your administrator.";
    case "TOTP_BAD_CODE":
      return "Invalid authenticator code.";
    default:
      return err.message || "Sign-in failed. Please try again.";
  }
}

// The logo artwork is drawn for a white background, so it sits on its own
// plate and stays legible on dark themes.
const logoPlateStyle: React.CSSProperties = {
  alignSelf: "center",
  marginBottom: 8,
  padding: "8px 12px",
  borderRadius: 8,
  background: "var(--logo-plate)",
};

const logoStyle: React.CSSProperties = {
  // The Bankom Group mark is 467x113 (4.1:1); 160px gives it the right visual weight.
  display: "block",
  width: 160,
  height: "auto",
};

const shellStyle: React.CSSProperties = {
  // Divided by the text-size zoom, which also scales viewport units inside .auth-shell.
  minHeight: "calc(100vh / var(--text-zoom))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--surface-2)",
  padding: 16,
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 400,
  background: "var(--surface)",
  padding: 32,
  borderRadius: 12,
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleStyle: React.CSSProperties = { margin: 0, color: "var(--brand-ink)", fontSize: 24 };
const subtitleStyle: React.CSSProperties = { margin: 0, color: "var(--ink-3)", fontSize: 14 };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, fontSize: 14 };
const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  fontSize: 15,
};
const primaryButton: React.CSSProperties = {
  padding: "12px 16px",
  background: "var(--brand-primary-600)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const linkStyle: React.CSSProperties = {
  color: "var(--brand-ink)",
  fontSize: 14,
  textDecoration: "none",
  textAlign: "center",
};
const errorStyle: React.CSSProperties = {
  padding: 12,
  background: "var(--err-bg)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 14,
};
